import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';

import { AccountService } from '@app/_services/account.service';
import { OAuthService } from 'angular-oauth2-oidc';
import { filter, map, of, switchMap, take } from 'rxjs';

export const AuthGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const router = inject(Router);
  const accountService = inject(AccountService);
  const oauthService = inject(OAuthService);

  const anyRoles: string[] | undefined = route.data.anyRoles;
  const isRoleRestricted = !!(route.data.role || anyRoles || route.data.forbiddenRole);

  return accountService.authInitialized$.pipe(
    switchMap(() => {
      if (!oauthService.hasValidAccessToken()) {
        return of(undefined);
      }

      if (!isRoleRestricted) {
        return of(accountService.userRoles ?? []);
      }

      // wait for roles, so guard can check (will override returnUrl from keycloak)
      return accountService.currentUserRoles$.pipe(
        filter((roles) => roles !== undefined),
        take(1),
      );
    }),
    map((currentRoles) => {
      if (currentRoles !== undefined) {
        // check if route is restricted by role
        if (
          (route.data.role && currentRoles.indexOf(route.data.role) === -1) ||
          (anyRoles && !anyRoles.some((r) => currentRoles.indexOf(r) !== -1)) ||
          (route.data.forbiddenRole && currentRoles.indexOf(route.data.forbiddenRole) !== -1)
        ) {
          // role not authorised so redirect to dashboard page
          router.navigate(['/home/dashboard']);
          return false;
        }

        // authorised so return true
        return true;
      }

      let queryParams = {};

      // returnUrl only for pages other than login and dashboard.
      if (state.url !== '/account/login' && state.url !== '/home/dashboard') {
        queryParams = { returnUrl: state.url };
      }

      // not logged in so redirect to login page with the return url
      accountService.updateUserRoles();
      router.navigate(['/account/login'], { queryParams: queryParams });
      return false;
    }),
  );
};
